import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, TextField, Button, InputAdornment, MenuItem, FormControl, InputLabel, Select, IconButton, Autocomplete, Typography } from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { StyledPaper } from '../styled/CommonStyles';
import ItemSearchDialog from './ItemSearchDialog';
import CompanySearchDialog from './CompanySearchDialog';
import BuyerSearchDialog from './BuyerSearchDialog';
import FactorySearchDialog from './FactorySearchDialog';
import ManagerSearchDialog from './ManagerSearchDialog';
import api from '../../api/api';
import { fetchDeliveryListCached, getDeliveryListCached } from '../../utils/cache/deliveryCache';
import { fetchManagerListCached, getManagerListCached } from '../../utils/cache/managerCache';

/** 고객사(벤더) 자동완성 필드 — 값으로 partner id를 풀 수 있음 */
const CUSTOMER_VENDOR_AUTOCOMPLETE_FIELDS = new Set([
    'customer',
    'customer_name',
    'company',
    'vendorName',
    'vendor',
    'vendorId'
]);

const MANAGER_AUTOCOMPLETE_FIELDS = new Set([
    'manager',
    'manager_name',
    'managerName',
    'contact'
]);

const normalizePartnerId = (id) => {
    if (id === undefined || id === null || id === '') return null;
    const n = Number(id);
    if (Number.isFinite(n) && String(id).trim() !== '') return n;
    return String(id);
};

const partnerIdsMatch = (managerPartnerId, selectedId) => {
    if (selectedId === null || selectedId === undefined) return false;
    if (managerPartnerId === undefined || managerPartnerId === null) return false;
    return String(managerPartnerId) === String(selectedId)
        || Number(managerPartnerId) === Number(selectedId);
};

const parsePartnerIdFromVendorOption = (opt) => {
    if (!opt) return null;
    if (opt.partnerId !== undefined && opt.partnerId !== null && opt.partnerId !== '') {
        return normalizePartnerId(opt.partnerId);
    }
    if (opt.partner_id !== undefined && opt.partner_id !== null && opt.partner_id !== '') {
        return normalizePartnerId(opt.partner_id);
    }
    const v = opt.value;
    if (v !== undefined && v !== null && v !== '') {
        const vs = String(v).trim();
        if (/^[0-9]+$/.test(vs)) return normalizePartnerId(vs);
    }
    const label = opt.label;
    if (typeof label === 'string') {
        const m = label.match(/^(\d+)\s*[-—]/);
        if (m) return normalizePartnerId(m[1]);
    }
    return null;
};

/**
 * 검색 파라미터·필드 정의에서 현재 선택된 고객사(partner) id를 구함.
 * 고객사가 비어 있으면 null → 담당자 전체 목록.
 */
const resolveSelectedCustomerPartnerId = (searchParams, searchFields) => {
    if (!searchParams || !searchFields) return null;

    const direct = searchParams.vendorId ?? searchParams.companyId ?? searchParams.customerId;
    if (direct !== undefined && direct !== null && direct !== '') {
        return normalizePartnerId(direct);
    }

    const customerFields = searchFields.filter(
        (f) => f.type === 'autocomplete' && CUSTOMER_VENDOR_AUTOCOMPLETE_FIELDS.has(f.name)
    );

    for (const f of customerFields) {
        const val = searchParams[f.name];
        if (val === undefined || val === null || String(val).trim() === '') continue;
        const strVal = String(val).trim();
        const options = f.options || [];
        const opt = options.find((o) => {
            if (String(o.value) === strVal) return true;
            if (String(o.label) === strVal) return true;
            if (o.partnerId != null && String(o.partnerId) === strVal) return true;
            if (o.partner_id != null && String(o.partner_id) === strVal) return true;
            return false;
        });
        if (opt) {
            const pid = parsePartnerIdFromVendorOption(opt);
            if (pid !== null) return pid;
        }
        if (/^[0-9]+$/.test(strVal)) {
            return normalizePartnerId(strVal);
        }
    }
    return null;
};

const managerAutocompleteUsesIdAsValue = (options) => {
    if (!options || options.length === 0) return false;
    const v = options[0].value;
    if (v === undefined || v === null || v === '') return false;
    return /^[0-9]+$/.test(String(v).trim());
};

const buildManagerOptionsFromCache = (filteredManagers, useIdAsValue) =>
    filteredManagers
        .map((m) => {
            const id = m.manager_id ?? m.id;
            const name = m.manager_name || m.name || '';
            if (useIdAsValue) {
                const vs = id !== undefined && id !== null ? String(id) : '';
                if (!vs) return null;
                return {
                    value: vs,
                    label: `${id} ${name}`.trim() || vs
                };
            }
            if (!name) return null;
            return {
                value: name,
                label: `${id || ''} ${name}`.trim() || String(id || '')
            };
        })
        .filter(Boolean);

// 전역 캐시를 위한 ref
const globalCache = {
    factories: null,
    managers: null,
    sizes: null,
    buyers: null,
    vendors: null,
    threads: null,
    loading: {
        factories: false,
        managers: false,
        sizes: false,
        buyers: false,
        vendors: false,
        threads: false
    }
};

const CommonFilterSection = ({
    title,
    searchParams,
    handleParamChange,
    handleSearch,
    handleReset,
    isLoading,
    searchDisabled = false,
    dateFields = [],
    searchFields = [],
    showDateFilter = true,
    showButtons = true,
    dateFilterLabel = '',
    filterHint = '',
    customStyles = {},
    onFieldChange, // 필드 변경 시 호출할 함수 (선택적)
    // 기존 스타일 컴포넌트들을 props로 받음
    StyledFilterSection,
    StyledSectionTitle,
    StyledDateFilterContainer,
    StyledButtonContainer
}) => {
    const { t, i18n } = useTranslation();
    
    // 언어별 날짜 형식 결정 (언어 변경 시 재계산되도록 useMemo 사용)
    const dateFormat = useMemo(() => {
        const language = i18n.language || 'ko';
        switch (language) {
            case 'ko':
                return 'YYYY-MM-DD';
            case 'en':
                return 'MM/DD/YYYY';
            case 'id':
                return 'DD/MM/YYYY';
            default:
                return 'YYYY-MM-DD';
        }
    }, [i18n.language]);
    const [searchDialogs, setSearchDialogs] = useState({});
    const [factories, setFactories] = useState([]);
    const [loadingFactories, setLoadingFactories] = useState(false);
    const [managers, setManagers] = useState([]);
    const [loadingManagers, setLoadingManagers] = useState(false);
    const [sizes, setSizes] = useState([]);
    const [loadingSizes, setLoadingSizes] = useState(false);

    const selectedCustomerPartnerId = useMemo(
        () => resolveSelectedCustomerPartnerId(searchParams, searchFields),
        [searchParams, searchFields]
    );

    const managersForCustomerFilter = useMemo(() => {
        if (selectedCustomerPartnerId == null) return null;
        return managers.filter((m) => partnerIdsMatch(m.partner_id, selectedCustomerPartnerId));
    }, [managers, selectedCustomerPartnerId]);

    // 공장 데이터 가져오기
    const fetchFactories = useCallback(async () => {
        if (globalCache.factories || globalCache.loading.factories) return; // 전역 캐시 확인
        
        // Global cache에서 먼저 확인
        const cachedData = getDeliveryListCached();
        if (cachedData && cachedData.length > 0) {
            globalCache.factories = cachedData;
            setFactories(cachedData);
            return;
        }
        
        console.log('fetchFactories');
        globalCache.loading.factories = true;
        setLoadingFactories(true);
        try {
            // Global cache를 사용하여 데이터 가져오기
            const data = await fetchDeliveryListCached(false);
            if (data && data.length > 0) {
                globalCache.factories = data;
                setFactories(data);
            }
        } catch (error) {
            console.error('공장 데이터 가져오기 실패:', error);
        } finally {
            globalCache.loading.factories = false;
            setLoadingFactories(false);
        }
    }, []);

    // 담당자 데이터 가져오기
    const fetchManagers = useCallback(async () => {
        if (globalCache.managers || globalCache.loading.managers) return; // 전역 캐시 확인
        
        // Global cache에서 먼저 확인
        const cachedData = getManagerListCached();
        if (cachedData && cachedData.length > 0) {
            globalCache.managers = cachedData;
            setManagers(cachedData);
            return;
        }
        
        globalCache.loading.managers = true;
        setLoadingManagers(true);
        try {
            // Global cache를 사용하여 데이터 가져오기
            const data = await fetchManagerListCached(false);
            if (data && data.length > 0) {
                globalCache.managers = data;
                setManagers(data);
            }
        } catch (error) {
            console.error('담당자 데이터 가져오기 실패:', error);
        } finally {
            globalCache.loading.managers = false;
            setLoadingManagers(false);
        }
    }, []);

    // 사이즈 데이터 가져오기
    const fetchSizes = useCallback(async () => {
        if (globalCache.sizes || globalCache.loading.sizes) return; // 전역 캐시 확인
        
        globalCache.loading.sizes = true;
        setLoadingSizes(true);
        try {
            const response = await api.get('/info/v2/size/list');
            if (response.data) {
                globalCache.sizes = response.data;
                setSizes(response.data);
            }
        } catch (error) {
            console.error('사이즈 데이터 가져오기 실패:', error);
        } finally {
            globalCache.loading.sizes = false;
            setLoadingSizes(false);
        }
    }, []);

    // 컴포넌트 마운트 시 필요한 데이터들을 초기에 한 번만 로드
    useEffect(() => {
        // searchFields에서 필요한 API들을 확인하여 초기 로드
        const hasFactoryField = searchFields.some(field => 
            field.name === 'factory' || field.name === 'delivery'
        );
        const hasManagerField = searchFields.some(field =>
            field.name === 'manager'
            || field.name === 'manager_name'
            || field.name === 'managerName'
            || field.name === 'contact'
        );
        const hasSizeField = searchFields.some(field => 
            field.name === 'size'
        );

        // Global cache에서 데이터가 있으면 먼저 설정
        if (hasFactoryField) {
            const cachedDeliveries = getDeliveryListCached();
            if (cachedDeliveries && cachedDeliveries.length > 0) {
                globalCache.factories = cachedDeliveries;
                setFactories(cachedDeliveries);
            } else if (globalCache.factories) {
                setFactories(globalCache.factories);
            }
        }
        if (hasManagerField) {
            const cachedManagers = getManagerListCached();
            if (cachedManagers && cachedManagers.length > 0) {
                globalCache.managers = cachedManagers;
                setManagers(cachedManagers);
            } else if (globalCache.managers) {
                setManagers(globalCache.managers);
            }
        }
        if (hasSizeField && globalCache.sizes) {
            setSizes(globalCache.sizes);
        }

        // 캐시에 데이터가 없으면 API 호출
        if (hasFactoryField && !globalCache.factories && !globalCache.loading.factories) {
            fetchFactories();
        }
        if (hasManagerField && !globalCache.managers && !globalCache.loading.managers) {
            fetchManagers();
        }
        if (hasSizeField && !globalCache.sizes && !globalCache.loading.sizes) {
            fetchSizes();
        }
    }, [searchFields, fetchFactories, fetchManagers, fetchSizes]); // searchFields와 fetch 함수들을 의존성에 포함

    // 공장 검색 다이얼로그 열기
    const handleFactorySearchOpen = useCallback(async () => {
        // Global cache에서 먼저 확인
        const cachedDeliveries = getDeliveryListCached();
        if (cachedDeliveries && cachedDeliveries.length > 0) {
            globalCache.factories = cachedDeliveries;
            setFactories(cachedDeliveries);
        } else if (!globalCache.factories && !globalCache.loading.factories) {
            // 전역 캐시에 데이터가 없고 로딩 중이 아니면 API 호출
            await fetchFactories();
        }
        setSearchDialogs(prev => ({
            ...prev,
            factory: true
        }));
    }, [fetchFactories]);

    // 담당자 검색 다이얼로그 열기
    const handleManagerSearchOpen = useCallback(async () => {
        // Global cache에서 먼저 확인
        const cachedManagers = getManagerListCached();
        if (cachedManagers && cachedManagers.length > 0) {
            globalCache.managers = cachedManagers;
            setManagers(cachedManagers);
        } else if (!globalCache.managers && !globalCache.loading.managers) {
            // 전역 캐시에 데이터가 없고 로딩 중이 아니면 API 호출
            await fetchManagers();
        }
        setSearchDialogs(prev => ({
            ...prev,
            manager: true,
            manager_name: true,
            managerName: true,
            contact: true
        }));
    }, [fetchManagers]);

    // 기본 스타일 컴포넌트들 (기존 스타일이 없을 경우 사용)
    const DefaultFilterSection = ({ children, ...props }) => (
        <div 
            style={{ 
                marginBottom: '24px', 
                width: '100%', 
                position: 'relative',
                ...props.style 
            }} 
            {...props}
        >
            {children}
        </div>
    );

    const DefaultSectionTitle = ({ children, ...props }) => (
        <h3 
            style={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                marginBottom: '16px',
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                margin: '0 0 16px 0'
            }} 
            {...props}
        >
            <span 
                style={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#1976d2',
                    borderRadius: '4px',
                    marginRight: '12px'
                }}
            />
            {children}
        </h3>
    );

    const DefaultDateFilterContainer = ({ children, ...props }) => (
        <div 
            style={{ 
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: '16px',
                ...props.style 
            }} 
            {...props}
        >
            {children}
        </div>
    );

    const DefaultButtonContainer = ({ children, ...props }) => (
        <div 
            style={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
                ...props.style 
            }} 
            {...props}
        >
            {children}
        </div>
    );

    // 사용할 스타일 컴포넌트 결정
    const FilterSectionComponent = StyledFilterSection || DefaultFilterSection;
    const SectionTitleComponent = StyledSectionTitle || DefaultSectionTitle;
    const DateFilterContainerComponent = StyledDateFilterContainer || DefaultDateFilterContainer;
    const ButtonContainerComponent = StyledButtonContainer || DefaultButtonContainer;

    // 검색 다이얼로그 열기
    const handleSearchDialogOpen = (fieldName) => {
        setSearchDialogs(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // 검색 다이얼로그 닫기
    const handleSearchDialogClose = (fieldName) => {
        setSearchDialogs(prev => ({
            ...prev,
            [fieldName]: false
        }));
    };

    // 검색 결과 선택 처리
    const getRelatedIdField = useCallback((fieldName) => {
        switch (fieldName) {
            case 'item':
            case 'threadName':
                return 'threadId';
            case 'customer':
            case 'customer_name':
            case 'company':
            case 'vendorName':
            case 'vendor':
            case 'vendorId':
                return 'vendorId';
            case 'buyer':
                return 'buyerId';
            case 'factory':
                return 'factoryId';
            case 'manager':
            case 'manager_name':
            case 'managerName':
            case 'contact':
                return 'managerId';
            default:
                return null;
        }
    }, []);

    const clearRelatedIdIfNeeded = useCallback((fieldName) => {
        const relatedIdField = getRelatedIdField(fieldName);
        if (relatedIdField && searchParams[relatedIdField]) {
            handleParamChange(relatedIdField, '');
        }
    }, [getRelatedIdField, handleParamChange, searchParams]);

    const handleSearchResultSelect = (fieldName, result) => {
        if (fieldName === 'item' || fieldName === 'threadName') {
            // thread_name을 표시용으로 사용하고, thread_id를 별도 파라미터로 저장
            handleParamChange(fieldName, result.thread_name);
            // thread_id를 별도 파라미터로 저장 (필터링용)
            handleParamChange('threadId', result.thread_id);
        } else if (fieldName === 'customer' || fieldName === 'customer_name' || fieldName === 'company' || fieldName === 'vendorName' || fieldName === 'vendor') {
            // customer_name을 표시용으로 사용하고, vendor_id를 별도 파라미터로 저장
            handleParamChange(fieldName, result.partner_name);
            // vendor_id를 별도 파라미터로 저장 (필터링용)
            handleParamChange('vendorId', result.partner_id);
            if (fieldName === 'company') {
                handleParamChange('companyId', result.partner_id);
            }
        } else if (fieldName === 'buyer') {
            // buyer_name을 표시용으로 사용하고, buyer_id를 별도 파라미터로 저장
            handleParamChange(fieldName, result.partner_name);
            // buyer_id를 별도 파라미터로 저장 (필터링용)
            handleParamChange('buyerId', result.partner_id);
        } else if (fieldName === 'factory') {
            // delivery_name을 표시용으로 사용하고, delivery_id를 별도 파라미터로 저장
            handleParamChange(fieldName, result.delivery_name);
            // delivery_id를 별도 파라미터로 저장 (필터링용)
            handleParamChange('factoryId', result.delivery_id);
        } else if (fieldName === 'manager' || fieldName === 'manager_name' || fieldName === 'managerName' || fieldName === 'contact') {
            // manager_name을 표시용으로 사용하고, manager_id를 별도 파라미터로 저장
            handleParamChange(fieldName, result.manager_name);
            // manager_id를 별도 파라미터로 저장 (필터링용)
            handleParamChange('managerId', result.manager_id);
        } else {
            handleParamChange(fieldName, result.name || result.value);
        }
        handleSearchDialogClose(fieldName);
    };

    // 검색 아이콘 렌더링
    const renderSearchIcon = (field) => {
        if (!field.showSearchIcon) return null;

        return (
            <IconButton
                size="small"
                onClick={() => handleSearchDialogOpen(field.name)}
                sx={{ ml: 1 }}
            >
                <SearchIcon />
            </IconButton>
        );
    };

    // 날짜 필드 렌더링
    const renderDateFields = () => {
        if (!showDateFilter || !dateFields.length) return null;

        return (
            <DateFilterContainerComponent>
                {dateFilterLabel ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap', fontWeight: 600, alignSelf: 'center' }}
                    >
                        {dateFilterLabel}
                    </Typography>
                ) : null}
                {dateFields.map((field, index) => (
                    <div key={field.key || index} style={{ width: '160px' }}>
                        {React.cloneElement(field.component, {
                            label: t(`common.${field.key === 'startDate' ? 'startDate' : 'endDate'}`),
                            value: searchParams[field.key],
                            onChange: (newValue) => {
                                handleParamChange(field.key, newValue);
                                if (
                                    field.key === 'endDate' &&
                                    newValue &&
                                    searchParams.startDate &&
                                    dayjs(newValue).isBefore(dayjs(searchParams.startDate), 'day')
                                ) {
                                    handleParamChange('startDate', newValue);
                                }
                            },
                            format: dateFormat,
                            maxDate:
                                field.key === 'startDate' && searchParams.endDate
                                    ? dayjs(searchParams.endDate)
                                    : undefined,
                            slotProps: {
                                textField: {
                                    size: "small",
                                    fullWidth: true
                                }
                            },
                            ...field.props
                        })}
                    </div>
                ))}
            </DateFilterContainerComponent>
        );
    };

    // 검색 필드 렌더링
    const renderSearchFields = () => {
        if (!searchFields.length) return null;

        return (
            <Grid container spacing={2}>
                {searchFields.map((field) => {
                    // threadName 필드를 item으로 매핑하여 처리
                    const fieldName = field.name === 'threadName' ? 'item' : field.name;
                    const fieldValue = searchParams[field.name] || '';
                    
                    // 엔터키 이벤트 처리
                    const handleKeyDown = (e) => {
                        if (e.key === 'Enter' && field.showSearchIcon && field.type !== 'autocomplete') {
                            e.preventDefault();
                            // threadName 필드의 경우 threadName 다이얼로그 상태를 직접 설정
                            if (field.name === 'threadName') {
                                setSearchDialogs(prev => ({
                                    ...prev,
                                    threadName: true
                                }));
                            } else if (field.name === 'factory') {
                                handleFactorySearchOpen();
                            } else if (
                                field.name === 'manager'
                                || field.name === 'manager_name'
                                || field.name === 'managerName'
                                || field.name === 'contact'
                            ) {
                                handleManagerSearchOpen();
                            } else {
                                handleSearchDialogOpen(fieldName);
                            }
                        }
                    };

                    if (field.type === 'select') {
                        // size 필드인 경우 API에서 가져온 데이터 사용
                        let options = field.options || [];
                        if (field.name === 'size') {
                            options = [
                                { value: '', label: t('common.all') },
                                ...sizes.map(size => ({
                                    value: size.size,
                                    label: size.size
                                }))
                            ];
                        }

                        return (
                            <Grid item xs={field.xs || 12} sm={field.sm || 6} md={field.md || 4} lg={field.lg || 2} key={field.name}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{field.label}</InputLabel>
                                    <Select
                                        key={`select-${field.name}`}
                                        value={fieldValue}
                                        onChange={(e) => handleParamChange(field.name, e.target.value)}
                                        label={field.label}
                                        onKeyDown={handleKeyDown}
                                        MenuProps={field.menuProps}
                                    >
                                        {options.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        );
                    }

                    if (field.type === 'autocomplete') {
                        let options = field.options || [];
                        if (MANAGER_AUTOCOMPLETE_FIELDS.has(field.name) && selectedCustomerPartnerId != null) {
                            if (!managers.length) {
                                options = [];
                            } else {
                                const filtered = managers.filter((m) =>
                                    partnerIdsMatch(m.partner_id, selectedCustomerPartnerId)
                                );
                                const useIdAsValue = managerAutocompleteUsesIdAsValue(field.options);
                                options = buildManagerOptionsFromCache(filtered, useIdAsValue);
                            }
                        }
                        // 빈 문자열일 때는 null로 처리하여 '전체' 옵션이 자동 선택되지 않도록 함
                        const selectedOption = fieldValue && fieldValue !== ''
                            ? options.find(opt => String(opt.value) === String(fieldValue)) || null
                            : null;

                        return (
                            <Grid item xs={field.xs || 12} sm={field.sm || 6} md={field.md || 4} lg={field.lg || 2} key={field.name}>
                                <Autocomplete
                                    freeSolo={field.freeSolo !== false}
                                    options={options}
                                    value={selectedOption}
                                    onChange={(event, newValue) => {
                                        const value = newValue ? (typeof newValue === 'string' ? newValue : newValue.value) : '';
                                        
                                        // 옵션을 선택했을 때만 onSelect 호출 (입력값이 옵션에 없을 때)
                                        if (field.onSelect && value && value.trim() !== '') {
                                            const matchedOption = options.find(
                                                opt => String(opt.value) === String(value) || opt.label === value
                                            );
                                            if (!matchedOption) {
                                                field.onSelect(value);
                                            }
                                        }
                                        
                                        if (onFieldChange) {
                                            onFieldChange(field.name, value);
                                        } else {
                                            handleParamChange(field.name, value);
                                        }
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (field.onInputChange) {
                                            field.onInputChange(event, newInputValue, reason, handleParamChange);
                                            return;
                                        }
                                        if (reason === 'clear') {
                                            if (onFieldChange) {
                                                onFieldChange(field.name, '');
                                            } else {
                                                handleParamChange(field.name, '');
                                            }
                                            return;
                                        }
                                        // 선택형(freeSolo: false): 타이핑은 옵션 필터만 하고, 값은 목록 선택(onChange) 시에만 반영
                                        if (field.freeSolo === false) {
                                            return;
                                        }
                                        if (reason === 'input') {
                                            if (newInputValue === '') {
                                                if (onFieldChange) {
                                                    onFieldChange(field.name, '');
                                                } else {
                                                    handleParamChange(field.name, '');
                                                }
                                                return;
                                            }

                                            const matchedOption = options.find(
                                                opt => opt.value === newInputValue || opt.label === newInputValue
                                            );
                                            if (matchedOption) {
                                                if (onFieldChange) {
                                                    onFieldChange(field.name, matchedOption.value);
                                                } else {
                                                    handleParamChange(field.name, matchedOption.value);
                                                }
                                            } else {
                                                if (onFieldChange) {
                                                    onFieldChange(field.name, newInputValue);
                                                } else {
                                                    handleParamChange(field.name, newInputValue);
                                                }
                                            }
                                        }
                                    }}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return option.label || String(option.value || '');
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        if (!value) return false;
                                        return String(option.value) === String(value.value || value);
                                    }}
                                    disabled={isLoading}
                                    ListboxProps={{
                                        ...(field.listboxProps || {}),
                                        sx: {
                                            fontSize: '0.8125rem',
                                            ...(field.listboxProps?.sx || {})
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={field.label}
                                            placeholder={field.placeholder}
                                            required={Boolean(field.required)}
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        );
                    }

                    if (field.type === 'date') {
                        return (
                            <Grid item xs={field.xs || 12} sm={field.sm || 6} md={field.md || 4} lg={field.lg || 2} key={field.name}>
                                <DatePicker
                                    label={field.label}
                                    value={fieldValue ? dayjs(fieldValue) : null}
                                    onChange={(newValue) => {
                                        handleParamChange(field.name, newValue);
                                    }}
                                    format={dateFormat}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    height: '40px'
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                        );
                    }

                    return (
                        <Grid item xs={field.xs || 12} sm={field.sm || 6} md={field.md || 4} lg={field.lg || 2} key={field.name}>
                            <TextField
                                key={`textfield-${field.name}`}
                                fullWidth
                                id={field.name}
                                name={field.name}
                                label={field.label}
                                value={fieldValue}
                                onChange={(e) => {
                                    if (field.showSearchIcon) {
                                        clearRelatedIdIfNeeded(field.name);
                                    }
                                    if (onFieldChange) {
                                        onFieldChange(field.name, e.target.value);
                                    } else {
                                        handleParamChange(field.name, e.target.value);
                                    }
                                }}
                                size="small"
                                onKeyDown={handleKeyDown}
                                InputProps={{
                                    endAdornment: field.showSearchIcon ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    // threadName 필드의 경우 threadName 다이얼로그 상태를 직접 설정
                                                    if (field.name === 'threadName') {
                                                        setSearchDialogs(prev => ({
                                                            ...prev,
                                                            threadName: true
                                                        }));
                                                    } else if (field.name === 'factory') {
                                                        handleFactorySearchOpen();
                                                    } else if (
                                                        field.name === 'manager'
                                                        || field.name === 'manager_name'
                                                        || field.name === 'managerName'
                                                        || field.name === 'contact'
                                                    ) {
                                                        handleManagerSearchOpen();
                                                    } else {
                                                        handleSearchDialogOpen(fieldName);
                                                    }
                                                }}
                                                edge="end"
                                                size="small"
                                                sx={{ color: 'action.active' }}
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                {...field.textFieldProps}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    // 검색 실행
    const handleSearchClick = () => {
        console.log('[CommonFilterSection] 검색 버튼 클릭', { searchParams });
        handleSearch();
    };

    // 버튼 렌더링
    const renderButtons = () => {
        if (!showButtons) return null;

        return (
            <ButtonContainerComponent>
                <Button
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    variant="outlined"
                    color="inherit"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    disabled={isLoading}
                >
                    {t('common.reset')} [F10]
                </Button>
                <Button
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}
                    startIcon={<SearchIcon />}
                    disabled={isLoading || searchDisabled}
                >
                    {t('common.search')} [F3]
                </Button>
            </ButtonContainerComponent>
        );
    };

    // 검색 다이얼로그 렌더링
    const renderSearchDialogs = () => {
        return (
            <>
                {/* 품목 검색 다이얼로그 (autocomplete가 아닌 경우에만) */}
                {searchFields.some(field => (field.name === 'item' || field.name === 'threadName') && field.showSearchIcon && field.type !== 'autocomplete') && (
                    <ItemSearchDialog
                        open={searchDialogs.item || searchDialogs.threadName}
                        onClose={() => {
                            handleSearchDialogClose('item');
                            handleSearchDialogClose('threadName');
                        }}
                        onSelect={(result) => {
                            // threadName 필드가 있는 경우 threadName으로 처리, 없으면 item으로 처리
                            const targetField = searchFields.some(field => field.name === 'threadName') ? 'threadName' : 'item';
                            handleSearchResultSelect(targetField, result);
                        }}
                        initialValue={searchParams.item || searchParams.threadName}
                    />
                )}

                {/* 고객사 검색 다이얼로그 */}
                {searchFields.some(field => (field.name === 'customer' || field.name === 'customer_name' || field.name === 'company' || field.name === 'vendorName' || field.name === 'vendor') && field.showSearchIcon) && (
                    <CompanySearchDialog
                        open={searchDialogs.customer || searchDialogs.customer_name || searchDialogs.company || searchDialogs.vendorName || searchDialogs.vendor}
                        onClose={() => {
                            handleSearchDialogClose('customer');
                            handleSearchDialogClose('customer_name');
                            handleSearchDialogClose('company');
                            handleSearchDialogClose('vendorName');
                            handleSearchDialogClose('vendor');
                        }}
                        onSelect={(result) => {
                            // vendor 필드가 있는 경우 vendor로 처리, customer_name 필드가 있는 경우 customer_name으로 처리, company 필드가 있으면 company로 처리, vendorName 필드가 있으면 vendorName으로 처리, 없으면 customer로 처리
                            let targetField = 'customer';
                            if (searchFields.some(field => field.name === 'vendor')) {
                                targetField = 'vendor';
                            } else if (searchFields.some(field => field.name === 'customer_name')) {
                                targetField = 'customer_name';
                            } else if (searchFields.some(field => field.name === 'company')) {
                                targetField = 'company';
                            } else if (searchFields.some(field => field.name === 'vendorName')) {
                                targetField = 'vendorName';
                            }
                            handleSearchResultSelect(targetField, result);
                        }}
                        initialValue={searchParams.customer || searchParams.customer_name || searchParams.company || searchParams.vendorName || searchParams.vendor}
                    />
                )}

                {/* 구매자 검색 다이얼로그 */}
                {searchFields.some(field => field.name === 'buyer' && field.showSearchIcon) && (
                    <BuyerSearchDialog
                        open={searchDialogs.buyer}
                        onClose={() => handleSearchDialogClose('buyer')}
                        onSelect={(result) => handleSearchResultSelect('buyer', result)}
                        initialValue={searchParams.buyer}
                    />
                )}

                {/* 공장 검색 다이얼로그 */}
                {searchFields.some(field => field.name === 'factory' && field.showSearchIcon) && (
                    <FactorySearchDialog
                        open={searchDialogs.factory}
                        onClose={() => handleSearchDialogClose('factory')}
                        onSelect={(result) => handleSearchResultSelect('factory', result)}
                        initialValue={searchParams.factory}
                        factories={factories}
                        loading={loadingFactories}
                    />
                )}

                {/* 담당자 검색 다이얼로그 */}
                {searchFields.some(field => (field.name === 'manager' || field.name === 'manager_name' || field.name === 'managerName' || field.name === 'contact') && field.showSearchIcon) && (
                    <ManagerSearchDialog
                        open={searchDialogs.manager || searchDialogs.manager_name || searchDialogs.managerName || searchDialogs.contact}
                        onClose={() => {
                            handleSearchDialogClose('manager');
                            handleSearchDialogClose('manager_name');
                            handleSearchDialogClose('managerName');
                            handleSearchDialogClose('contact');
                        }}
                        onSelect={(result) => {
                            // contact 필드가 있는 경우 contact로 처리, manager_name 필드가 있는 경우 manager_name으로 처리, managerName 필드가 있으면 managerName으로 처리, 없으면 manager로 처리
                            let targetField = 'manager';
                            if (searchFields.some(field => field.name === 'contact')) {
                                targetField = 'contact';
                            } else if (searchFields.some(field => field.name === 'manager_name')) {
                                targetField = 'manager_name';
                            } else if (searchFields.some(field => field.name === 'managerName')) {
                                targetField = 'managerName';
                            }
                            handleSearchResultSelect(targetField, result);
                        }}
                        initialValue={searchParams.manager || searchParams.manager_name || searchParams.managerName || searchParams.contact}
                        managers={
                            managersForCustomerFilter != null ? managersForCustomerFilter : managers
                        }
                        loading={loadingManagers}
                    />
                )}
            </>
        );
    };

    return (
        <FilterSectionComponent style={customStyles}>
            {title && (
                <SectionTitleComponent>
                    {title}
                </SectionTitleComponent>
            )}

            {renderDateFields()}

            <StyledPaper elevation={0} sx={{ mt: { xs: 8, md: 0 } }}>
                {filterHint ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {filterHint}
                    </Typography>
                ) : null}
                {renderSearchFields()}
                {renderButtons()}
            </StyledPaper>

            {renderSearchDialogs()}
        </FilterSectionComponent>
    );
};

export default CommonFilterSection;
